import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { ContentSection, H2, H4 } from '../components/ui'
import { SEO } from '../components/SEO/SEO'

export const PrivacyPolicy = () => {
  const seoConfig = {
    title: 'Privacy Policy - Et Ai Labs Data Protection',
    description:
      'Et Ai Labs privacy policy. Learn how we protect your data, ensure scientific research privacy, and comply with GDPR and Australian Privacy Act.',
    keywords:
      'Et Ai privacy policy, data protection, GDPR compliance, research data privacy, academic privacy',
    url: 'https://etai.app/privacy',
    canonical: 'https://etai.app/privacy',
    type: 'article',
    publishedTime: '2025-07-02',
    modifiedTime: '2025-07-02',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      description: 'Et Ai Labs privacy policy and data protection practices',
      publisher: {
        '@type': 'Organization',
        name: 'Et Ai Labs',
        url: 'https://etai.app',
      },
    },
  }

  return (
    <StaticPageLayout title="Privacy Policy">
      <SEO {...seoConfig} />
      <ContentSection prose>
        <p className="text-gray-400 mb-6">Last updated: July 2, 2025</p>

        <section className="mb-8">
          <H2>1. Our Commitment to Your Privacy</H2>
          <p className="text-gray-300 mb-4">
            Welcome to <strong>Et Ai Labs Pty Ltd</strong> (also referred to as &quot;Et Ai
            Labs,&quot; &quot;Et Ai,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). Our
            mission is to accelerate scientific discovery, and we believe the currency of science is
            trust. This principle guides our approach to data privacy. We are committed to
            protecting your personal data and being transparent about how we use it.
          </p>
          <p className="text-gray-300 mb-4">
            This policy explains how we collect, use, and protect your information when you use our
            services (the &quot;Services&quot;). It is designed to comply with major data protection
            regulations, including the EU&apos;s General Data Protection Regulation (GDPR) and the
            Australian Privacy Act.
          </p>
          <p className="text-gray-300 mb-4">
            <strong>Data Controller:</strong> Et Ai Labs Pty Ltd
            <br />
            <strong>Contact for Privacy Matters:</strong> privacy@etai.app
          </p>
        </section>

        <section className="mb-8">
          <H2>2. The Information We Collect</H2>
          <p className="text-gray-300 mb-4">
            To provide and improve our Services, we collect the following categories of personal
            data:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Account and Identity Data:</strong> Your first name, last name, email address,
              username, and encrypted password.
            </li>
            <li>
              <strong>Query and Service Content:</strong> The scientific queries, text, facts, and
              documents you submit to our Services for analysis. We treat this information as highly
              sensitive.
            </li>
            <li>
              <strong>Payment Data:</strong> When you subscribe to a paid tier, our third-party
              payment processor, Stripe, will collect your payment card information. We do not
              directly store or have access to your full credit card details.
            </li>
            <li>
              <strong>Technical Data:</strong> Your IP address, browser type and version, device
              information, and login data for security and operational purposes.
            </li>
            <li>
              <strong>Usage Data:</strong> Information about how you use our Services, such as
              features used, frequency of logins, and user-interface interactions.
            </li>
          </ul>
          <p className="text-gray-300 mb-4">
            We do <strong>not</strong> collect any Special Categories of Personal Data
            (racial/ethnic origin, political opinions, religious beliefs, health data, etc.). Our
            services are not designed to process this information.
          </p>
        </section>

        <section className="mb-8">
          <H2>3. How We Use Your Information</H2>
          <p className="text-gray-300 mb-4">
            We process your data for specific, defined purposes and on clear legal grounds.
          </p>

          <H4 className="mt-6">a) To Provide the Service (Contractual Necessity)</H4>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Account Management:</strong> We use your Account Data to create and maintain
              your account, authenticate you, and provide customer support.
            </li>
            <li>
              <strong>Processing Your Queries:</strong> We use your{' '}
              <strong>Query and Service Content</strong> solely to generate the results you request
              from our Services.{' '}
              <strong>
                Your queries are not manually read by our staff except where required for direct
                technical support initiated by you.
              </strong>
            </li>
          </ul>

          <H4 className="mt-6">
            b) To Improve Our Services (Legitimate Interests with User Control)
          </H4>
          <p className="text-gray-300 mb-4">
            To build a better, more accurate, and more useful platform, we analyze how our service
            is used.
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>To understand which features are most valuable to researchers.</li>
            <li>To identify broad trends in scientific inquiry to inform our product roadmap.</li>
            <li>
              To improve the performance, accuracy, and reliability of our AI models and algorithms.
            </li>
            <li>To monitor for and prevent security threats.</li>
          </ul>
          <div className="border-l-4 border-gray-500 pl-4 italic text-gray-400 my-6">
            <strong>Your Control Over This Processing:</strong> We believe in privacy by design. The
            processing of your Query and Service Content for service improvement is performed on an{' '}
            <strong>anonymized and aggregated basis</strong> and does not involve associating your
            identity with the content of your queries for analytical purposes. Most importantly,{' '}
            <strong>
              you can opt out of having your Query and Service Content used for service improvement
              at any time.
            </strong>{' '}
            This option is available in your account settings and is enabled by default to help us
            build the best possible service.
          </div>

          <H4 className="mt-6">c) For Communications</H4>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              We use your Contact Data to send essential service-related announcements (e.g.,
              changes to our terms, security notices).
            </li>
            <li>
              We will only send you marketing communications with your explicit, opt-in consent.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <H2>4. Information Sharing and International Transfers</H2>
          <p className="text-gray-300 mb-4">
            We do not sell your personal data. We only share your data with the following trusted
            third-party service providers (&quot;subprocessors&quot;) who help us operate our
            platform:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Cloud Hosting Providers:</strong> We use{' '}
              <strong>OVHCloud, Amazon Web Services (AWS), and Google Cloud</strong> for our core
              server infrastructure and data processing.
            </li>
            <li>
              <strong>Backend and Database Platform:</strong> We use <strong>Supabase</strong> to
              store and manage user data and analytics information.
            </li>
            <li>
              <strong>Payment Processor:</strong> We use <strong>Stripe</strong> to securely process
              payments for our subscription services.
            </li>
          </ul>
          <p className="text-gray-300 my-4">
            These providers are bound by strict contractual obligations to protect your data and are
            prohibited from using your personal data for any purpose other than providing services
            to us. We may also share data if required by law or in connection with a business
            transfer, as detailed below.
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Legal Requirements:</strong> If required by law, such as in response to a
              subpoena or court order.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, sale, or
              acquisition, your data may be transferred as a business asset. We will notify you
              before your data is transferred and becomes subject to a different privacy policy.
            </li>
          </ul>
          <p className="text-gray-300 mb-4">
            <strong>International Transfers:</strong> Our service providers may be located outside
            of your home country. When we transfer your data outside jurisdictions like the EEA or
            Australia, we ensure appropriate safeguards are in place, such as Standard Contractual
            Clauses (SCCs) approved by the relevant authorities.
          </p>
        </section>

        <section className="mb-8">
          <H2>5. Data Security and Retention</H2>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Security:</strong> We implement robust technical and organizational measures
              to protect your data, including encryption of data in transit (TLS) and at rest
              (AES-256), regular security assessments, and strict access controls.
            </li>
            <li>
              <strong>Retention:</strong> We retain your personal data only for as long as
              necessary. Typically, this means as long as you have an active account with us. Data
              processed for service improvement is anonymized. When you delete your account, your
              personal data is permanently and securely deleted from our production systems in
              accordance with our data deletion timelines.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <H2>6. Your Data Protection Rights</H2>
          <p className="text-gray-300 mb-4">
            You have the following rights over your personal data:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Access, Rectification, and Erasure:</strong> You can access, update, or
              request deletion of your personal information directly within your account settings or
              by contacting us.
            </li>
            <li>
              <strong>Object to Processing:</strong> You can object to our processing of your data
              for legitimate interests. You can exercise your right to object to processing for
              service improvement at any time by disabling the &quot;Service Improvement&quot;
              toggle in your account settings.
            </li>
            <li>
              <strong>Data Portability:</strong> You can request a copy of your personal data in a
              machine-readable format.
            </li>
            <li>
              <strong>Withdraw Consent:</strong> Where we rely on your consent (e.g., for
              marketing), you can withdraw it at any time.
            </li>
            <li>
              <strong>Lodge a Complaint:</strong> You have the right to file a complaint with your
              local data protection authority.
            </li>
          </ul>
          <p className="text-gray-300 mt-4 mb-4">
            To exercise any of these rights, please contact us at privacy@etai.app. We will respond
            within one month.
          </p>
        </section>

        <section className="mb-8">
          <H2>7. Cookies and Tracking</H2>
          <p className="text-gray-300 mb-4">
            We use essential cookies required for our service to function (e.g., to keep you logged
            in). We use analytics cookies to understand website traffic. We will only use marketing
            or advertising cookies with your explicit, opt-in consent. You can manage your
            preferences via our cookie consent banner. A detailed Cookie Policy will be available on
            our website.
          </p>
        </section>

        <section className="mb-8">
          <H2>8. Children&apos;s Privacy</H2>
          <p className="text-gray-300 mb-4">
            Our Services are intended for a professional and academic audience and are not directed
            at individuals under the age of 16. We do not knowingly collect data from children.
          </p>
        </section>

        <section className="mb-8">
          <H2>9. Changes to This Policy</H2>
          <p className="text-gray-300 mb-4">
            We may update this policy to reflect changes in our practices or for legal reasons. We
            will notify you of any material changes by posting the new policy on this page and, for
            significant changes, by sending an email notification.
          </p>
        </section>

        <section className="mb-8">
          <H2>10. Contact Information</H2>
          <p className="text-gray-300 mb-4">For any questions, please contact:</p>
          <p className="text-gray-300 mb-4">
            <strong>Data Controller:</strong>
            <br />
            Et Ai Labs Pty Ltd
            <br />
            <strong>Registered Address:</strong>
            <br />
            26 Lower River Terrace, South Brisbane, QLD 4101, Australia
            <br />
            <strong>Email:</strong>
            <br />
            privacy@etai.app
          </p>
        </section>
      </ContentSection>
    </StaticPageLayout>
  )
}
